import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import Swiper from 'swiper';
import type { SwiperOptions } from 'swiper/types';
import { Navigation, Pagination } from 'swiper/modules';
import constants from '../shared/constants';

Swiper.use([Pagination, Navigation]);

const swiperConfig: SwiperOptions = {
    slidesPerView: 'auto',
    spaceBetween: 20,
    watchOverflow: true,
    navigation: {
        nextEl: constants.targets.swiper.navigationNext,
        prevEl: constants.targets.swiper.navigationPrev,
        addIcons: false,
    },
    pagination: {
        el: constants.targets.swiper.pagination,
        type: 'fraction',
        renderFraction: (currentClass, totalClass) => {
            return `<span class="${currentClass}"></span><span></span>/<span class="${totalClass}"></span>`;
        }
    },
    breakpoints: {
        [constants.breakpoints.desktop]: {
            spaceBetween: 32,
        },
    },
};

function initSwiper($slider: HTMLElement, overrides: SwiperOptions = {}): void {
    const $scope = $slider.parentElement ?? $slider;
    const navigationConfig = typeof swiperConfig.navigation === 'object' ? swiperConfig.navigation : {};
    const paginationConfig = typeof swiperConfig.pagination === 'object' ? swiperConfig.pagination : {};

    new Swiper($slider, {
        ...swiperConfig,
        ...overrides,
        navigation: {
            ...navigationConfig,
            ...(typeof overrides.navigation === 'object' ? overrides.navigation : {}),
            nextEl: $scope.querySelector<HTMLElement>(constants.targets.swiper.navigationNext),
            prevEl: $scope.querySelector<HTMLElement>(constants.targets.swiper.navigationPrev),
        },
        pagination: {
            ...paginationConfig,
            ...(typeof overrides.pagination === 'object' ? overrides.pagination : {}),
            el: $scope.querySelector<HTMLElement>(constants.targets.swiper.pagination),
        },
    });
}

document.addEventListener('DOMContentLoaded', (): void => {
    const $imageVideoSliders: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>(constants.targets.swiper.imageVideoSlider);

    $imageVideoSliders.forEach(($slider: HTMLElement) => {
        initSwiper($slider);
    });

    const $agendaSliders: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>(constants.targets.swiper.agendaSlider);

    $agendaSliders.forEach(($slider: HTMLElement) => {
        const agendaConfig: SwiperOptions = {
            slidesPerView: 1.2,
            breakpoints: {
                ...swiperConfig.breakpoints,
                [constants.breakpoints.mobile]: {
                    slidesPerView: 1.8,
                },
                [constants.breakpoints.tablet]: {
                    slidesPerView: 2.4,
                },
                [constants.breakpoints.desktop]: {
                    slidesPerView: 3,
                },
            },
        };

        initSwiper($slider, agendaConfig);
    });

    const $newsSliders: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>(constants.targets.swiper.newsSlider);

    $newsSliders.forEach(($slider: HTMLElement) => {
        const newsConfig: SwiperOptions = {
            slidesPerView: 1.2,
            breakpoints: {
                ...swiperConfig.breakpoints,
                [constants.breakpoints.mobile]: {
                    slidesPerView: 1.8,
                },
                [constants.breakpoints.tablet]: {
                    slidesPerView: 2.4,
                },
                [constants.breakpoints.desktop]: {
                    slidesPerView: 3,
                },
            },
        };

        initSwiper($slider, newsConfig);
    });

    const $vacancySliders: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>(constants.targets.swiper.vacancySlider);

    $vacancySliders.forEach(($slider: HTMLElement) => {
        const vacancyConfig: SwiperOptions = {
            breakpoints: {
                [constants.breakpoints.desktop]: {
                    spaceBetween: 20,
                },
            },
        };

        initSwiper($slider, vacancyConfig);
    });

    const $styleguideSliders: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>(constants.targets.swiper.styleguideSlider);

    $styleguideSliders.forEach(($slider: HTMLElement) => {
        const styleguideConfig: SwiperOptions = {
            slidesPerView: 1.2,
            breakpoints: {
                ...swiperConfig.breakpoints,
                [constants.breakpoints.mobile]: {
                    slidesPerView: 1.8,
                },
                [constants.breakpoints.tablet]: {
                    slidesPerView: 2.4,
                },
                [constants.breakpoints.desktop]: {
                    ...swiperConfig.breakpoints?.[constants.breakpoints.desktop],
                    slidesPerView: 3,
                },
                [constants.breakpoints.widescreen]: {
                    slidesPerView: 4,
                },
            },
        };

        initSwiper($slider, styleguideConfig);
    });
});
